import React, { useState } from 'react';
import { Calendar, Brain, Lightbulb, BookmarkPlus, ExternalLink } from 'lucide-react';
import { WhatsappShareButton, WhatsappIcon, FacebookShareButton, FacebookIcon } from 'react-share';
import { Topic } from '../types';
import { useStore } from '../store';

interface TopicCardProps {
  topic: Topic;
}

interface LinkInfo {
  url: string;
  text: string;
}

export const TopicCard: React.FC<TopicCardProps> = ({ topic }) => {
  const { saveFAQ } = useStore();
  const [linkInfo, setLinkInfo] = useState<LinkInfo>({
    url: `https://www.google.com/search?q=${encodeURIComponent(`מתי כדאי ${topic.question}`)}`,
    text: 'חיפוש מידע נוסף'
  });

  const handleSaveToFAQ = async () => {
    try {
      const success = await saveFAQ(topic);
      if (success) {
        alert('השאלה נשמרה בהצלחה במאגר השאלות הנפוצות');
      } else {
        throw new Error('Failed to save FAQ');
      }
    } catch (error) {
      console.error('Error saving FAQ:', error);
      alert('אירעה שגיאה בשמירת השאלה. אנא נסו שוב מאוחר יותר.');
    }
  };

  const shareText = `מתי כדאי ${topic.question}?\n${topic.bestTime}\n\n${topic.explanation}${topic.tip ? `\n\nטיפ: ${topic.tip}` : ''}`;
  const shareUrl = window.location.href;

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow duration-300">
      <div className="flex justify-between items-start mb-6">
        <button
          onClick={handleSaveToFAQ}
          className="text-blue-500 hover:text-blue-600 transition-colors"
          title="שמור כשאלה נפוצה"
        >
          <BookmarkPlus className="w-5 h-5" />
        </button>
        <h3 className="text-xl font-bold text-right text-gray-800 border-b pb-4 flex-1">
          מתי כדאי {topic.question}?
        </h3>
      </div>
      
      <div className="space-y-6">
        <div className="flex items-start justify-end gap-4 text-right">
          <div className="flex-1">
            <div className="font-semibold text-gray-800 text-lg mb-1">{topic.bestTime}</div>
            <div className="text-sm text-gray-500">התקופה המומלצת</div>
          </div>
          <Calendar className="text-blue-500 flex-shrink-0 w-6 h-6 mt-1" />
        </div>

        <div className="flex items-start justify-end gap-4 text-right">
          <div className="flex-1">
            <div className="text-gray-700 leading-relaxed">{topic.explanation}</div>
          </div>
          <Brain className="text-purple-500 flex-shrink-0 w-6 h-6 mt-1" />
        </div>

        {topic.tip && (
          <div className="flex items-start justify-end gap-4 text-right bg-yellow-50 p-4 rounded-lg">
            <div className="flex-1">
              <div className="text-gray-700">{topic.tip}</div>
            </div>
            <Lightbulb className="text-yellow-500 flex-shrink-0 w-6 h-6 mt-1" />
          </div>
        )}

        <div className="flex items-center justify-between gap-2 mt-4 pt-4 border-t">
          <div className="flex items-center gap-2">
            <WhatsappShareButton url={shareUrl} title={shareText}>
              <WhatsappIcon size={32} round />
            </WhatsappShareButton>
            <FacebookShareButton url={shareUrl} quote={shareText}>
              <FacebookIcon size={32} round />
            </FacebookShareButton>
          </div>

          <a
            href={linkInfo.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-blue-600 hover:text-blue-800 transition-colors text-sm"
          >
            <span>{linkInfo.text}</span>
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      </div>
    </div>
  );
};